import { FC } from 'react';
import { Button, Typography } from '@mui/material';
import { ManageHistoryOutlined as ManageHistoryOutlinedIcon } from '@mui/icons-material';
import { DataGridProProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import moment from 'moment-timezone';

import Column from '../../../../../components/Column';
import DataGridPro from '../../../../../components/CDGridPro';
import Row from '../../../../../components/Row';

import { CampaignChangeRequest } from '../../../../../types/Campaign';
import CampaignChangeRequestStatus from '../../../../../features/Campaign/CampaignChangeRequestStatus';

interface CampaignChangeRequestAction {
    name: string;
    callback: (campaignChangeRequest: CampaignChangeRequest) => void;
}

type CampaignChangeRequestDataGridProps = Omit<DataGridProProps, 'columns' | 'rows'> & {
    rows: CampaignChangeRequest[];
    action: CampaignChangeRequestAction;
};

const NoRowsOverlay: FC = () => {
    return (
        <Column gap={2} justifyContent="center" flex={1} height="100%">
            <Row justifyContent="center">
                <ManageHistoryOutlinedIcon sx={{ color: 'action.disabledBackground', fontSize: 80 }} />
            </Row>

            <Row justifyContent="center">
                <Typography variant="subtitle2" color="text.secondary">
                    Change History is not available
                </Typography>
            </Row>
        </Column>
    );
};

const CampaignChangeRequestDataGrid: FC<CampaignChangeRequestDataGridProps> = ({ rows, action, ...props }) => {
    const columns: GridColDef[] = [
        {
            flex: 1,
            filterable: false,
            field: 'state',
            headerName: 'Status',
            renderCell: ({ row }: GridRenderCellParams) => {
                return <CampaignChangeRequestStatus value={row.status} />;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'createdByUser.name',
            headerName: 'Requested By',
            valueGetter: (params, row: CampaignChangeRequest) => {
                return row?.createdByUser?.name || row?.createdByUser?.emailAddress || ' - ';
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'approvedByUser.name',
            headerName: 'Reviewed By',
            valueGetter: (params, row: CampaignChangeRequest) => {
                return row?.approvedByUser?.name || row?.approvedByUser?.emailAddress || ' - ';
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'createdDate',
            headerName: 'Date of Request',
            renderCell: ({ row }: GridRenderCellParams) => {
                return moment(row.createdDate).format('MMMM DD, YYYY');
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'approvedDate',
            headerName: 'Date of Review',
            renderCell: ({ row }: GridRenderCellParams) => {
                return row.approvedDate ? moment(row.approvedDate).format('MMMM DD, YYYY') : ' - ';
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'source',
            headerName: 'Source',
            renderCell: ({ row }: GridRenderCellParams) => {
                return row.source ? row.source : ' - ';
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'right',
            align: 'right',
            renderCell: ({ row }: GridRenderCellParams) => {
                return (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                            action.callback(row);
                        }}
                    >
                        {row?.status === 'Pending' ? action.name : 'View Details'}
                    </Button>
                );
            },
        },
    ];

    const getRowId = (row: CampaignChangeRequest): string | number => {
        return row.id as number;
    };

    return (
        <DataGridPro
            // Overridable props
            {...props}
            disableColumnPinning
            getRowId={getRowId}
            columns={columns}
            rows={rows}
            slots={{
                toolbar: null,
                noRowsOverlay: NoRowsOverlay,
            }}
            sx={{
                '--DataGrid-overlayHeight': '212px',
            }}
        />
    );
};
export default CampaignChangeRequestDataGrid;
